/**
 * Générateurs des ITEMS NATIONAUX : 4e, Résolution de problèmes, campagne 2026.
 * Même contrat que 2026/6e/automatismes.js (le modèle) : un générateur par item, sa fiche en tête
 * (lue sur la CAPTURE de l'item), ITEMS avec la bonne réponse officielle exacte, CATEGORIES, generate.
 *
 * Une variante garde l'histoire de l'item, sa tâche, son nombre d'étapes et ses pièges : chaque mauvaise
 * réponse officielle est l'effet d'une erreur, recalculée sur les valeurs tirées. Les choix sont des
 * chaînes (texte ou KaTeX), le juste en premier dans la liste donnée à makeQcm, qui les mélange.
 * Les domaines de valeurs sont calculés au chargement, par les contraintes de chaque item : un tirage y
 * prend ses valeurs sans boucle de rejet, et l'item officiel est toujours l'une des variantes.
 * Les figures sont des SVG faits maison, conformes aux valeurs tirées, et ne donnent jamais la réponse.
 * Une personne nommée n'est jamais reprise par « il » ou « elle » : son prénom, ou une tournure sans
 * pronom (les items Q03, Q10, Q12 et Q16 en avaient).
 */
import { randInt, randomChoice, shuffle, makeQcm, tex, fracTex } from '../../../utils';
import { normalizeAnswer } from '../../../../utils/questionUtils';
import { enLettres } from '../../lettres';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: null, label });

/** Le second chemin de calcul : une variante fausse ne sort jamais (FORMATS § 0.2). */
const verifier = (condition, message) => {
    if (!condition) throw new Error(`Items 4e, problèmes : ${message}`);
};

/** Un énoncé HTML, morceau par morceau. */
const html = (...morceaux) => morceaux.join('');

/** Une figure SVG de l'énoncé (FORMATS § 12), en data-URL. */
const image = (svg, alt, maxWidth) => ({
    type: 'image',
    config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, alt, maxWidth },
});

const svgRacine = (largeur, hauteur, alt, contenu) => html(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${largeur} ${hauteur}" width="${largeur}"`,
    ` role="img" aria-label="${alt}" font-family="Arial, Helvetica, sans-serif" fill="#1f2937">`,
    contenu,
    '</svg>',
);

/**
 * Le QCM d'une variante : quatre choix en chaînes, le juste en premier, tous différents après la
 * normalisation du correcteur (FORMATS § 0.1).
 */
const qcm = (choix, texte, label, figures = null) => {
    verifier(choix.length === 4 && choix.every((c) => typeof c === 'string'), `quatre choix en texte : ${choix.join(' | ')}`);
    verifier(new Set(choix.map(normalizeAnswer)).size === 4, `choix confondus : ${choix.join(' | ')}`);
    const options = figures ? { ...opts(label), elements_additionnels: { version: 1, elements: figures } } : opts(label);
    return makeQcm(choix, choix[0], texte, options);
};

/** Un nombre de dixièmes écrit à la française : 25 → « 2,5 », 20 → « 2 ». */
const dixiemes = (n) => (n % 10 === 0 ? String(n / 10) : `${Math.floor(n / 10)},${n % 10}`);

/** Un montant en centimes : 630 → « 6,30 € ». */
const euros = (centimes) => `${Math.floor(centimes / 100)},${String(centimes % 100).padStart(2, '0')} €`;

/**
 * Q01 : prix de plusieurs kilogrammes (une étape, proportionnalité).
 * Item : « Dans un supermarché, les fraises sont à 2 € le kilogramme. Combien vais-je payer pour
 *   acheter 5 kg de fraises dans ce supermarché ? » ; 10 € (juste), 5 €, 6 €, 7 €.
 * Domaine : l'item, 2 € le kilogramme et 5 kg, un produit des tables. Variantes : un prix entier de
 *   2 à 5 € le kilogramme, une masse entière de 3 à 9 kg, un produit de 6 à 45 €. La masse n'est ni
 *   le prix ni le prix moins 1, pour que chaque piège garde son sens (sinon « masse + 1 » serait aussi
 *   le prix d'un kilogramme). 23 variantes.
 * Erreurs : 5 € : la masse prise pour le prix ; 7 € : le prix et la masse additionnés (2 + 5) ;
 *   6 € : erreur non identifiée, TRANCHÉ LE 10/10 (on garde), reproduite au plus près : la masse plus 1, le choix
 *   voisin de la masse, entre la masse et la somme.
 */
const L01 = 'Prix de plusieurs kilogrammes';
const DOMAINE_Q01 = [];
for (let prix = 2; prix <= 5; prix += 1) {
    for (let masse = 3; masse <= 9; masse += 1) {
        if (masse !== prix && masse + 1 !== prix) DOMAINE_Q01.push({ prix, masse });
    }
}
const genQ01 = () => {
    const { prix, masse } = randomChoice(DOMAINE_Q01);
    const juste = prix * masse;
    let somme = 0;
    for (let kg = 0; kg < masse; kg += 1) somme += prix;
    verifier(somme === juste, 'Q01, le prix total');
    return qcm(
        [`${juste} €`, `${masse} €`, `${masse + 1} €`, `${prix + masse} €`],
        html(
            `<p>Dans un supermarché, les fraises sont à ${prix} € le kilogramme.</p>`,
            `<p><b>Combien vais-je payer pour acheter ${masse} kg de fraises dans ce supermarché ?</b></p>`,
        ),
        L01,
    );
};

/**
 * Q02 : côté manquant d'un terrain, connaissant son périmètre (plusieurs étapes, additif, partie-tout).
 * Item : « Le dessin ci-dessous représente le plan d'un terrain clos. [plan : un pentagone en forme de
 *   maison ; le bas et les deux murs mesurent 50 m, le côté gauche du toit 20 m, le côté droit du toit
 *   n'a pas de mesure] On connait la longueur de quatre des côtés du terrain. Le périmètre de ce
 *   terrain est de 210 m. Quelle est la longueur du cinquième côté ? » ; 40 m (juste), 210 m, 170 m,
 *   30 m.
 * Domaine : l'item, un bas et deux murs de 50 m, un toit de 20 m et 40 m, des dizaines. Variantes :
 *   la même maison, le bas et les deux murs égaux, de 40 à 80 m (dizaines), les deux côtés du toit
 *   de 10 à 95 m (multiples de 5), le côté mesuré du toit plus court que le bas d'au moins 10 m ;
 *   le toit existe et se dessine : son sommet tombe entre les deux murs, à une hauteur de 20 à 70 %
 *   de la largeur ; le côté cherché n'est égal à aucun autre côté. Le plan est à l'échelle, comme
 *   celui de l'item, et le périmètre est relu sur le dessin.
 * Erreurs : 210 m : le périmètre recopié ; 170 m : la somme des quatre côtés connus, la soustraction
 *   oubliée ; 30 m : 50 − 20, le côté cherché complète le côté de 20 m pour égaler le bas, comme si
 *   les deux côtés du toit mesuraient ensemble la largeur du terrain.
 */
const L02 = 'Côté manquant, connaissant le périmètre';
const DOMAINE_Q02 = [];
for (let w = 40; w <= 80; w += 10) {
    for (let t1 = 10; t1 <= w - 10; t1 += 5) {
        for (let t2 = 10; t2 <= 95; t2 += 5) {
            const xA = (t1 * t1 - t2 * t2 + w * w) / (2 * w);
            const h2 = t1 * t1 - xA * xA;
            const yA = h2 > 0 ? Math.sqrt(h2) : 0;
            if (t2 !== t1 && t2 !== w && xA >= 0.15 * w && xA <= 0.85 * w && yA >= 0.2 * w && yA <= 0.7 * w) {
                DOMAINE_Q02.push({ w, t1, t2, xA, yA });
            }
        }
    }
}
const planTerrain = ({ w, t1, xA, yA }, alt) => {
    const haut = w + yA;
    const k = Math.min(220 / w, 250 / haut);
    const ox = 64;
    const oy = 26;
    // bas gauche, bas droit, haut droit, sommet du toit, haut gauche ; en mètres, y vers le haut
    const metres = [[0, 0], [w, 0], [w, w], [xA, haut], [0, w]];
    const pts = metres.map(([x, y]) => [ox + x * k, oy + (haut - y) * k]);
    const cx = pts.reduce((s, p) => s + p[0], 0) / pts.length;
    const cy = pts.reduce((s, p) => s + p[1], 0) / pts.length;
    const cote = (i, j, texte) => {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[j];
        const mx = (x1 + x2) / 2;
        const my = (y1 + y2) / 2;
        const lg = Math.hypot(x2 - x1, y2 - y1);
        let nx = (y2 - y1) / lg;
        let ny = (x1 - x2) / lg;
        if (nx * (mx - cx) + ny * (my - cy) < 0) {
            nx = -nx;
            ny = -ny;
        }
        let ancre = 'middle';
        if (nx > 0.5) ancre = 'start';
        if (nx < -0.5) ancre = 'end';
        return `<text x="${(mx + nx * 10).toFixed(1)}" y="${(my + ny * 12 + 5).toFixed(1)}" text-anchor="${ancre}" font-size="14" font-weight="700">${texte}</text>`;
    };
    const contenu = html(
        `<polygon points="${pts.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}" fill="#e0e1f2" stroke="#3d4fa6" stroke-width="2.5" stroke-linejoin="round"/>`,
        cote(0, 1, `${w} m`),
        cote(1, 2, `${w} m`),
        cote(4, 0, `${w} m`),
        cote(3, 4, `${t1} m`),
    );
    return { metres, svg: svgRacine(Math.round(ox * 2 + w * k), Math.round(oy + haut * k + 36), alt, contenu) };
};
const genQ02 = () => {
    const terrain = randomChoice(DOMAINE_Q02);
    const { w, t1, t2 } = terrain;
    const perimetre = 3 * w + t1 + t2;
    const alt = `Plan d'un terrain à cinq côtés en forme de maison : le bas et les deux murs mesurent ${w} m, un côté du toit ${t1} m, l'autre côté du toit n'a pas de mesure.`;
    const { metres, svg } = planTerrain(terrain, alt);
    const longueurs = metres.map((p, i) => Math.hypot(metres[(i + 1) % 5][0] - p[0], metres[(i + 1) % 5][1] - p[1]));
    verifier(Math.abs(longueurs.reduce((s, l) => s + l, 0) - perimetre) < 1e-6, 'Q02, le périmètre du dessin');
    verifier(Math.abs(longueurs[2] - t2) < 1e-6 && Math.abs(longueurs[3] - t1) < 1e-6, 'Q02, les côtés du toit');
    return qcm(
        [`${t2} m`, `${perimetre} m`, `${perimetre - t2} m`, `${w - t1} m`],
        html(
            '<p>Le dessin ci-dessous représente le plan d\'un terrain clos.</p>{{element:0}}',
            `<p>On connait la longueur de quatre des côtés du terrain. Le périmètre de ce terrain est de ${perimetre} m.</p>`,
            '<p><b>Quelle est la longueur du cinquième côté ?</b></p>',
        ),
        L02,
        [image(svg, alt, 'small')],
    );
};

/**
 * Q03 : durée d'un trajet plus rapide (une étape, multiplicatif, comparaison).
 * Item : « César et Romain habitent à la même distance du collège. Romain vient au collège à pied.
 *   Sa vitesse est de 5 km/h. César vient à vélo. Il est trois fois plus rapide. Le trajet de Romain
 *   dure 12 minutes. Combien dure le trajet de César ? » ; 4 min (juste), 36 min, 15 min, 3 min.
 *   « Il est trois fois plus rapide » devient « César, qui vient à vélo, est trois fois plus rapide ».
 * Domaine : l'item, 5 km/h (donnée inutile), trois fois plus rapide, 12 minutes, une réponse
 *   entière. Variantes : une vitesse à pied de 4 à 6 km/h, toujours inutile ; deux, trois ou quatre
 *   fois plus rapide ; un trajet à pied de 6 à 30 minutes, multiple du coefficient ; la durée
 *   cherchée vaut au moins 2 minutes et n'est ni la vitesse ni un autre choix ; la vitesse de César
 *   n'est pas la durée de Romain.
 * Erreurs : 36 min : la durée multipliée par 3 au lieu d'être divisée ; 15 min : la vitesse de César,
 *   5 × 3 = 15 km/h, donnée pour la durée, TRANCHÉ LE 10/10 (on garde) : l'autre lecture, 12 + 3 (« trois fois plus »
 *   lu « 3 de plus »), donne aussi 15 sur l'item ; 3 min : erreur non identifiée, TRANCHÉ LE 10/10 (on garde),
 *   reproduite au plus près : le coefficient « trois » pris pour la durée.
 */
const L03 = 'Durée d\'un trajet plus rapide';
const DOMAINE_Q03 = [];
for (let v = 4; v <= 6; v += 1) {
    for (let k = 2; k <= 4; k += 1) {
        for (let tR = 6; tR <= 30; tR += 1) {
            const tC = tR / k;
            if (tR % k === 0 && tC >= 2 && tC !== v && v * k !== tR && new Set([tC, tR * k, v * k, k]).size === 4) {
                DOMAINE_Q03.push({ v, k, tR, tC });
            }
        }
    }
}
const genQ03 = () => {
    const { v, k, tR, tC } = randomChoice(DOMAINE_Q03);
    // second calcul : la distance (v × tR minutes) parcourue à la vitesse v × k
    verifier((v * tR) % (v * k) === 0 && (v * tR) / (v * k) === tC, 'Q03, la durée de César');
    return qcm(
        [`${tC} min`, `${tR * k} min`, `${v * k} min`, `${k} min`],
        html(
            '<p>César et Romain habitent à la même distance du collège. Romain vient au collège à pied. ',
            `Sa vitesse est de ${v} km/h. César, qui vient à vélo, est ${enLettres(k)} fois plus rapide. `,
            `Le trajet de Romain dure ${tR} minutes.</p>`,
            '<p><b>Combien dure le trajet de César ?</b></p>',
        ),
        L03,
    );
};

/**
 * Q04 : prix d'une quantité, connaissant deux achats (deux étapes, multiplicatif, proportionnalité).
 * Item : « Dans une même boulangerie, Lucas achète 7 pains au chocolat et paie 6,30 €, Enzo achète
 *   9 pains au chocolat et paie 8,10 €. Combien paierait Camillia pour 8 pains au chocolat ? » ;
 *   7,20 € (juste), 7,10 €, 0,90 €, 7,30 €. La capture écrit Camillia, la base Camilla.
 * Domaine : l'item, un pain à 0,90 €, 7 et 9 pains achetés, 8 demandés (entre les deux). Variantes :
 *   un pain de 0,80 € à 1,50 €, par dizaines de centimes, jamais 1 € (les deux pièges « 1 € par
 *   pain » tomberaient sur la bonne réponse) ; Lucas achète de 3 à 8 pains, Enzo deux de plus,
 *   Camillia un de plus que Lucas. 42 variantes.
 * Erreurs : 0,90 € : le prix d'un pain, la première étape seulement ; 7,30 € : le prix de Lucas plus
 *   1 €, un pain de plus compté 1 € ; 7,10 € : le prix d'Enzo moins 1 €, un pain de moins compté 1 €.
 */
const L04 = 'Prix d\'une quantité, connaissant deux achats';
const DOMAINE_Q04 = [];
[80, 90, 110, 120, 130, 140, 150].forEach((unite) => {
    for (let lucas = 3; lucas <= 8; lucas += 1) DOMAINE_Q04.push({ unite, lucas });
});
const genQ04 = () => {
    const { unite, lucas } = randomChoice(DOMAINE_Q04);
    const enzo = lucas + 2;
    const camillia = lucas + 1;
    const juste = camillia * unite;
    // second calcul : Camillia achète la moyenne des deux quantités et paie la moyenne des deux prix
    verifier((lucas * unite + enzo * unite) / 2 === juste, 'Q04, le prix de Camillia');
    return qcm(
        [euros(juste), euros(unite), euros(lucas * unite + 100), euros(enzo * unite - 100)],
        html(
            `<p>Dans une même boulangerie, Lucas achète ${lucas} pains au chocolat et paie ${euros(lucas * unite)}, `,
            `Enzo achète ${enzo} pains au chocolat et paie ${euros(enzo * unite)}.</p>`,
            `<p><b>Combien paierait Camillia pour ${camillia} pains au chocolat ?</b></p>`,
        ),
        L04,
    );
};

/**
 * Q05 : prix de 15 objets, connaissant celui de 10 (une ou deux étapes, proportionnalité).
 * Item : « Louise a acheté 10 objets identiques pour 22 €. Combien coûtent 15 de ces objets ? » ;
 *   33 € (juste), 47 €, 15 €, 27 €.
 * Domaine : l'item, 10 objets pour 22 €, 15 demandés, une réponse entière. Variantes : 10 objets puis
 *   15, comme l'item ; un prix pair de 12 à 58 €, pour une réponse entière ; jamais 50 € (le piège
 *   10 + 50 + 15 tomberait sur la bonne réponse, 75 €). 23 variantes.
 * Erreurs : 47 € : tous les nombres de l'énoncé additionnés (10 + 22 + 15) ; 15 € : le nombre
 *   d'objets demandé pris pour le prix ; 27 € : 22 + 5, raisonnement additif (5 objets de plus,
 *   5 € de plus).
 */
const L05 = 'Prix de 15 objets, connaissant celui de 10';
const DOMAINE_Q05 = [];
for (let prix = 12; prix <= 58; prix += 2) {
    if (prix !== 50) DOMAINE_Q05.push(prix);
}
const genQ05 = () => {
    const prix = randomChoice(DOMAINE_Q05);
    const juste = (prix * 3) / 2;
    // second calcul : le prix d'un objet en centimes (prix × 10), fois 15
    verifier(prix * 10 * 15 === juste * 100, 'Q05, le prix de 15 objets');
    return qcm(
        [`${juste} €`, `${10 + prix + 15} €`, '15 €', `${prix + 5} €`],
        html(
            `<p>Louise a acheté 10 objets identiques pour ${prix} €.</p>`,
            '<p><b>Combien coûtent 15 de ces objets ?</b></p>',
        ),
        L05,
    );
};

/**
 * Q06 : le tiers d'une distance (une étape, fraction d'une grandeur).
 * Item : « Une randonnée cyclotouriste est organisée sur un parcours de 60 km. Un arrêt est prévu au
 *   tiers de ce parcours. Quelle est la distance parcourue avant cet arrêt ? » ; 20 km (juste),
 *   10 km, 12 km, 15 km.
 * Domaine : l'item, le tiers de 60 km, quatre choix entiers. Adapté, TRANCHÉ LE 10/10 (on garde) : le tiers d'un
 *   parcours de 30 à 150 km ; pour que le quart, le cinquième et le sixième restent entiers comme dans
 *   l'item, il faudrait un multiple de 60 (60 ou 120 km seulement). Le parcours est donc un multiple
 *   de 30 ou de 12 : le tiers et le sixième sont entiers, le quart ou le cinquième peut avoir une
 *   décimale (7,5 km ; 14,4 km). 13 variantes.
 * Erreurs : 15 km : le quart au lieu du tiers (60 ÷ 4) ; 12 km et 10 km : erreur non identifiée,
 *   TRANCHÉ LE 10/10 (on garde), reproduite sous la même forme : le parcours divisé par 5 et par 6, une autre fraction
 *   unitaire que le tiers.
 */
const L06 = 'Le tiers d\'une distance';
const DOMAINE_Q06 = [];
for (let d = 30; d <= 150; d += 6) {
    if (d % 30 === 0 || d % 12 === 0) DOMAINE_Q06.push(d);
}
const genQ06 = () => {
    const d = randomChoice(DOMAINE_Q06);
    const parts = [3, 4, 5, 6].map((n) => (d * 10) / n);
    verifier(parts.every(Number.isInteger), 'Q06, une décimale au plus');
    verifier(parts[0] * 3 === d * 10, 'Q06, le tiers');
    return qcm(
        parts.map((p) => `${dixiemes(p)} km`),
        html(
            `<p>Une randonnée cyclotouriste est organisée sur un parcours de ${d} km. Un arrêt est prévu au tiers de ce parcours.</p>`,
            '<p><b>Quelle est la distance parcourue avant cet arrêt ?</b></p>',
        ),
        L06,
    );
};

/**
 * Q07 : aire d'une zone sur un plan quadrillé (deux étapes, additif et multiplicatif).
 * Item : « Le plan de ce terrain indique une zone boisée. [plan quadrillé : terrain de 10 carreaux sur
 *   6 ; zone boisée de 36 carreaux ; légende : un carré de 2 carreaux sur 2 vaut 1 km², un carreau à
 *   arbres figure la zone boisée] La superficie de la zone boisée est de » ; 9 km² (juste), 36 km²,
 *   60 km², 4 km².
 * Domaine : l'item, un terrain de 10 carreaux sur 6, une zone de 36 carreaux, 4 carreaux par km².
 *   Variantes : un terrain de 9 à 12 carreaux sur 5 à 7 (50 à 72 carreaux) ; une zone d'un seul tenant,
 *   sans trou, qui n'est pas un rectangle, de 45 à 70 % du terrain, d'un nombre de carreaux multiple
 *   de 4 (réponse de 6 à 12 km², jamais 4) ; la légende de l'item (1 km² = 2 carreaux sur 2). La zone
 *   pousse case par case au hasard ; repli vérifié : la zone de l'item.
 * Erreurs : 36 km² : les carreaux comptés comme des km² ; 60 km² : tous les carreaux du terrain
 *   comptés comme des km² ; 4 km² : erreur non identifiée, TRANCHÉ LE 10/10 (on garde), reproduite telle quelle : le
 *   nombre de carreaux d'un km² dans la légende.
 */
const L07 = 'Aire d\'une zone sur un plan quadrillé';
const DOMAINE_Q07 = [];
[[10, 6], [9, 6], [11, 6], [10, 5], [12, 5], [10, 7], [12, 6]].forEach(([W, H]) => {
    for (let n = 24; n <= 0.7 * W * H; n += 4) {
        if (n >= 0.45 * W * H && n !== 16) DOMAINE_Q07.push({ W, H, n });
    }
});
const ZONE_ITEM = ['.XXXXXX...', '.XXXXXX...', 'XXXXXXXXX.', '.XXXXXXXXX', '..X...X...', '..XX..XX..'];
const zoneDeLItem = () => {
    const cases = new Set();
    ZONE_ITEM.forEach((ligne, y) => [...ligne].forEach((c, x) => {
        if (c === 'X') cases.add(y * 10 + x);
    }));
    return { W: 10, H: 6, n: 36, zone: cases };
};
const VOISINS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
/** Une zone de n carreaux d'un seul tenant, qui pousse case par case depuis un carreau du milieu. */
const croitreZone = (W, H, n) => {
    const cases = [];
    const dans = new Set();
    const ajouter = (i) => {
        dans.add(i);
        cases.push(i);
    };
    ajouter(randInt(1, H - 2) * W + randInt(2, W - 3));
    for (let pas = 0; pas < 4000 && cases.length < n; pas += 1) {
        const i = randomChoice(cases);
        const [dx, dy] = randomChoice(VOISINS);
        const x = (i % W) + dx;
        const y = Math.floor(i / W) + dy;
        if (x >= 0 && x < W && y >= 0 && y < H && !dans.has(y * W + x)) ajouter(y * W + x);
    }
    return cases.length === n ? dans : null;
};
/** Un carreau vide enfermé par la zone (un trou) : il n'est pas atteint depuis le bord du terrain. */
const aUnTrou = (dans, W, H) => {
    const vus = new Set();
    const pile = [];
    for (let x = 0; x < W; x += 1) pile.push(x, (H - 1) * W + x);
    for (let y = 0; y < H; y += 1) pile.push(y * W, y * W + W - 1);
    while (pile.length > 0) {
        const i = pile.pop();
        if (!vus.has(i) && !dans.has(i)) {
            vus.add(i);
            const x = i % W;
            if (x > 0) pile.push(i - 1);
            if (x < W - 1) pile.push(i + 1);
            if (i >= W) pile.push(i - W);
            if (i < (H - 1) * W) pile.push(i + W);
        }
    }
    return vus.size + dans.size < W * H;
};
const estRectangle = (dans, W) => {
    const xs = [...dans].map((i) => i % W);
    const ys = [...dans].map((i) => Math.floor(i / W));
    return (Math.max(...xs) - Math.min(...xs) + 1) * (Math.max(...ys) - Math.min(...ys) + 1) === dans.size;
};
const COTE_Q07 = 24;
/** Les petits arbres d'un carreau : quatre étoiles à six branches, comme sur le plan de l'item. */
const arbres = (x, y) => [[6, 6], [18, 6], [6, 18], [18, 18]]
    .map(([dx, dy]) => `M${x + dx} ${y + dy - 3}v6M${x + dx - 2.6} ${y + dy - 1.5}l5.2 3M${x + dx - 2.6} ${y + dy + 1.5}l5.2 -3`)
    .join('');
const planZone = (W, H, zone, alt) => {
    const S = COTE_Q07;
    const M = 14;
    const colonnes = W + 6;
    const X = (c) => M + c * S;
    const Y = (r) => M + r * S;
    let grille = '';
    for (let c = 0; c <= colonnes; c += 1) grille += `M${X(c)} ${Y(0)}V${Y(H)}`;
    for (let r = 0; r <= H; r += 1) grille += `M${X(0)} ${Y(r)}H${X(colonnes)}`;
    let fonds = '';
    let marques = '';
    let bord = '';
    zone.forEach((i) => {
        const c = i % W;
        const r = Math.floor(i / W);
        fonds += `M${X(c)} ${Y(r)}h${S}v${S}h-${S}Z`;
        marques += arbres(X(c), Y(r));
        if (r === 0 || !zone.has(i - W)) bord += `M${X(c)} ${Y(r)}h${S}`;
        if (r === H - 1 || !zone.has(i + W)) bord += `M${X(c)} ${Y(r + 1)}h${S}`;
        if (c === 0 || !zone.has(i - 1)) bord += `M${X(c)} ${Y(r)}v${S}`;
        if (c === W - 1 || !zone.has(i + 1)) bord += `M${X(c + 1)} ${Y(r)}v${S}`;
    });
    const lx = X(W + 1);
    const rangEchantillon = H >= 6 ? 4 : 3;
    const ye = Y(rangEchantillon);
    const contenu = html(
        `<rect x="0" y="0" width="${2 * M + colonnes * S}" height="${2 * M + H * S}" fill="#ffffff"/>`,
        `<path d="${grille}" stroke="#c9ced6" stroke-width="1" fill="none"/>`,
        `<path d="${fonds}" fill="#dcf2d3"/>`,
        `<path d="${marques}" stroke="#2f8f3a" stroke-width="1.1" fill="none"/>`,
        `<path d="${bord}" stroke="#2f9e44" stroke-width="2" fill="none" stroke-linecap="square"/>`,
        `<rect x="${X(0)}" y="${Y(0)}" width="${W * S}" height="${H * S}" fill="none" stroke="#111827" stroke-width="2.5"/>`,
        `<text x="${lx}" y="${Y(0) + 17}" font-size="13">Légende :</text>`,
        `<rect x="${lx}" y="${Y(1)}" width="${2 * S}" height="${2 * S}" fill="#e5e7eb" stroke="#111827" stroke-width="2"/>`,
        `<text x="${lx + S}" y="${Y(2) + 5}" text-anchor="middle" font-size="13">1 km²</text>`,
        `<path d="M${lx} ${ye}h${S}v${S}h-${S}Z" fill="#dcf2d3" stroke="#2f9e44" stroke-width="2"/>`,
        `<path d="${arbres(lx, ye)}" stroke="#2f8f3a" stroke-width="1.1" fill="none"/>`,
        `<text x="${lx + S + 6}" y="${ye + 17}" font-size="13">zone boisée</text>`,
    );
    return svgRacine(2 * M + colonnes * S, 2 * M + H * S, alt, contenu);
};
const genQ07 = () => {
    let plan = null;
    const { W, H, n } = randomChoice(DOMAINE_Q07);
    for (let essai = 0; essai < 30 && plan === null; essai += 1) {
        const zone = croitreZone(W, H, n);
        if (zone !== null && !aUnTrou(zone, W, H) && !estRectangle(zone, W)) plan = { W, H, n, zone };
    }
    if (plan === null) plan = zoneDeLItem();
    // second calcul : les carreaux de la zone recomptés ligne par ligne
    let compte = 0;
    for (let y = 0; y < plan.H; y += 1) {
        for (let x = 0; x < plan.W; x += 1) {
            if (plan.zone.has(y * plan.W + x)) compte += 1;
        }
    }
    verifier(compte === plan.n && compte === plan.zone.size && compte % 4 === 0, 'Q07, les carreaux de la zone');
    verifier(!aUnTrou(plan.zone, plan.W, plan.H), 'Q07, une zone sans trou');
    const alt = `Plan quadrillé d'un terrain rectangulaire de ${plan.W} carreaux sur ${plan.H}, avec une zone boisée délimitée en vert. Légende : un carré de 2 carreaux sur 2 représente 1 km².`;
    return qcm(
        [`${compte / 4} km²`, `${compte} km²`, `${plan.W * plan.H} km²`, '4 km²'],
        html(
            '<p>Le plan de ce terrain indique une zone boisée.</p>{{element:0}}',
            '<p><b>La superficie de la zone boisée est de…</b></p>',
        ),
        L07,
        [image(planZone(plan.W, plan.H, plan.zone, alt), alt, 'large')],
    );
};

/**
 * Q08 : la saveur qui a 25 % de chances (plusieurs étapes, mixte additif et multiplicatif).
 * Item : « Une boîte de macarons (biscuits fourrés) contient 3 macarons au café, 3 à la fraise, 6 au
 *   chocolat et 4 à la pomme. On prend un macaron les yeux fermés, sans avoir vu la boîte auparavant.
 *   On a 25 % de chances d'attraper un macaron… » ; à la pomme. (juste), au café., au chocolat.,
 *   à la fraise. La parenthèse explicative est retirée (FORMATS § 0.5).
 * Domaine : l'item, 16 macarons, 4 à la pomme (le quart), 6 au chocolat, 3 et 3 pour les autres.
 *   Variantes : les quatre saveurs de l'item, dans le même ordre ; une boîte de 12 à 28 macarons
 *   (multiple de 4) ; une saveur a exactement le quart des macarons, une en a plus (le quart plus 2
 *   à 4), deux en ont moins (le quart moins 1 ou 2, au moins 2) ; la saveur juste est tirée.
 * Erreurs : au chocolat. : la saveur la plus nombreuse, choisie sans calculer de proportion ; au café.
 *   et à la fraise. : erreur non identifiée, TRANCHÉ LE 10/10 (on garde) (quatre saveurs lues « 25 % chacune ») ; les
 *   variantes gardent une saveur plus nombreuse et deux moins nombreuses que la juste.
 */
const L08 = 'Saveur qui a 25 % de chances';
const SAVEURS = [
    { texte: 'au café', choix: 'au café.' },
    { texte: 'à la fraise', choix: 'à la fraise.' },
    { texte: 'au chocolat', choix: 'au chocolat.' },
    { texte: 'à la pomme', choix: 'à la pomme.' },
];
const DOMAINE_Q08 = [];
for (let total = 12; total <= 28; total += 4) {
    const quart = total / 4;
    [quart - 2, quart - 1].forEach((p1) => [quart - 2, quart - 1].forEach((p2) => {
        if (p1 >= 2 && p2 >= 2) DOMAINE_Q08.push({ total, quart, petits: [p1, p2], grand: 3 * quart - p1 - p2 });
    }));
}
const genQ08 = () => {
    const { total, quart, petits, grand } = randomChoice(DOMAINE_Q08);
    const [juste, plusNombreuse, autre1, autre2] = shuffle([0, 1, 2, 3]);
    const nombres = [];
    nombres[juste] = quart;
    nombres[plusNombreuse] = grand;
    nombres[autre1] = petits[0];
    nombres[autre2] = petits[1];
    verifier(nombres.reduce((s, x) => s + x, 0) === total, 'Q08, le nombre de macarons');
    verifier(nombres.filter((x) => x * 4 === total).length === 1 && nombres[juste] * 4 === total, 'Q08, une seule saveur à 25 %');
    verifier(grand > quart && petits.every((p) => p < quart), 'Q08, une saveur plus nombreuse, deux moins nombreuses');
    return qcm(
        [juste, plusNombreuse, autre1, autre2].map((i) => SAVEURS[i].choix),
        html(
            `<p>Une boîte de macarons contient ${nombres[0]} macarons ${SAVEURS[0].texte}, ${nombres[1]} ${SAVEURS[1].texte}, `,
            `${nombres[2]} ${SAVEURS[2].texte} et ${nombres[3]} ${SAVEURS[3].texte}. `,
            'On prend un macaron les yeux fermés, sans avoir vu la boîte auparavant.</p>',
            '<p><b>On a 25 % de chances d\'attraper un macaron…</b></p>',
        ),
        L08,
    );
};

/**
 * Q09 : largeur d'un rectangle, connaissant son périmètre (deux étapes).
 * Item : « Anna marche 100 m pour faire le tour de son jardin rectangulaire. La longueur de ce jardin
 *   est de 30 m. Quelle est la largeur ? » ; 20 m (juste), 35 m, 25 m, 40 m.
 * Domaine : l'item, un tour de 100 m, une longueur de 30 m, une largeur de 20 m. Variantes : un tour
 *   de 60 à 160 m (multiple de 20, le quart est entier), une longueur paire plus grande que le quart
 *   du tour ; une largeur d'au moins le tiers de la longueur et plus courte qu'elle d'au moins 4 m ;
 *   jamais un tour de trois fois la longueur ni de 8/3 de la longueur (deux pièges y tomberaient sur
 *   la même valeur). 34 variantes.
 * Erreurs : 35 m : (100 − 30) ÷ 2, une seule longueur retirée ; 25 m : 100 ÷ 4, le jardin pris pour
 *   un carré ; 40 m : 100 − 2 × 30, la division par 2 oubliée.
 */
const L09 = 'Largeur d\'un rectangle, connaissant son périmètre';
const DOMAINE_Q09 = [];
for (let tour = 60; tour <= 160; tour += 20) {
    for (let longueur = 2; longueur < tour / 2; longueur += 2) {
        const largeur = tour / 2 - longueur;
        if (longueur > tour / 4 && 3 * largeur >= longueur && largeur <= longueur - 4 && tour !== 3 * longueur && 8 * longueur !== 3 * tour) {
            DOMAINE_Q09.push({ tour, longueur, largeur });
        }
    }
}
const genQ09 = () => {
    const { tour, longueur, largeur } = randomChoice(DOMAINE_Q09);
    verifier(2 * (longueur + largeur) === tour, 'Q09, le tour du jardin');
    return qcm(
        [`${largeur} m`, `${(tour - longueur) / 2} m`, `${tour / 4} m`, `${tour - 2 * longueur} m`],
        html(
            `<p>Anna marche ${tour} m pour faire le tour de son jardin rectangulaire. La longueur de ce jardin est de ${longueur} m.</p>`,
            '<p><b>Quelle est la largeur ?</b></p>',
        ),
        L09,
    );
};

/**
 * Q10 : somme de deux fractions d'un gâteau (une étape, additif).
 * Item : « Tom a mangé 1/2 gâteau. Jean a mangé 1/4 du même gâteau. À eux deux, quelle fraction du
 *   gâteau ont-ils mangée ? » ; 3/4 (juste), 2/6, 2/4, 1/6. La question devient « Quelle fraction du
 *   gâteau a été mangée en tout ? », sans pronom ; « 1/2 gâteau » devient « 1/2 du gâteau », juste
 *   pour toutes les fractions.
 * Domaine : l'item, 1/2 + 1/4. Variantes : deux fractions unitaires, un dénominateur multiple de
 *   l'autre, au plus 12 ; la somme écrite sur le grand dénominateur est irréductible, comme 3/4 :
 *   1/2 + 1/4, 1/2 + 1/8, 1/2 + 1/12, 1/3 + 1/9, 1/3 + 1/12, 1/4 + 1/8, 1/5 + 1/10 ; Tom a l'une
 *   ou l'autre. 14 variantes. Fractions en KaTeX, dans l'énoncé comme dans les choix.
 * Erreurs : 2/6 : numérateurs et dénominateurs additionnés ; 2/4 : le dénominateur commun pris sans
 *   convertir le numérateur de 1/2 (1 + 1 sur 4) ; 1/6 : les dénominateurs additionnés, le
 *   numérateur 1 gardé.
 */
const L10 = 'Somme de deux fractions d\'un gâteau';
const pgcd = (a, b) => (b === 0 ? a : pgcd(b, a % b));
const DOMAINE_Q10 = [];
for (let petit = 2; petit <= 6; petit += 1) {
    for (let grand = 2 * petit; grand <= 12; grand += petit) {
        if (pgcd(grand / petit + 1, grand) === 1) DOMAINE_Q10.push([petit, grand]);
    }
}
const genQ10 = () => {
    const [petit, grand] = randomChoice(DOMAINE_Q10);
    const [tom, jean] = randomChoice([[petit, grand], [grand, petit]]);
    const num = grand / petit + 1;
    // second calcul : 1/p + 1/g = (g + p)/(p × g), égal à num/g, irréductible
    verifier((grand + petit) * grand === num * petit * grand && pgcd(num, grand) === 1, 'Q10, la somme');
    return qcm(
        [fracTex(num, grand), fracTex(2, petit + grand), fracTex(2, grand), fracTex(1, petit + grand)],
        html(
            `<p>Tom a mangé ${fracTex(1, tom)} du gâteau. Jean a mangé ${fracTex(1, jean)} du même gâteau.</p>`,
            '<p><b>Quelle fraction du gâteau a été mangée en tout ?</b></p>',
        ),
        L10,
    );
};

/**
 * Q11 : lait pour 100 g de beurre (une étape, multiplicatif, valeur d'une part, conversion).
 * Item : « Avec 20 L de lait on obtient 1 kg de beurre. Pour obtenir 100 g de beurre il faut … de
 *   lait. » ; 2 L (juste), 2000 L, 20 cL, 200 L.
 * Domaine : l'item, 20 L pour 1 kg, 100 g demandés, une réponse entière, 2 L. Adapté, TRANCHÉ LE 10/10 (on garde) :
 *   de 15 à 30 L de lait pour 1 kg de beurre, toujours 100 g demandés ; la réponse est le dixième,
 *   décimale quand la quantité n'est pas un multiple de 10 (2,4 L) : avec des réponses entières,
 *   seuls 20 et 30 L restent réalistes pour du beurre. 16 variantes.
 * Erreurs : 2000 L : 20 × 100, le « 100 » de 100 g pris pour un coefficient multiplicateur ; 200 L :
 *   20 × 10, le bon rapport dans le mauvais sens ; 20 cL : 20 ÷ 100 = 0,2 L, comme si 1 kg valait
 *   100 g.
 */
const L11 = 'Lait pour 100 g de beurre';
const DOMAINE_Q11 = [];
for (let lait = 15; lait <= 30; lait += 1) DOMAINE_Q11.push(lait);
const genQ11 = () => {
    const lait = randomChoice(DOMAINE_Q11);
    // second calcul en centilitres : 100 × lait cL pour 1000 g, donc 10 × lait cL pour 100 g
    verifier((100 * lait * 100) / 1000 === 10 * lait, 'Q11, le lait pour 100 g');
    return qcm(
        [`${dixiemes(lait)} L`, `${lait * 100} L`, `${lait * 10} L`, `${lait} cL`],
        html(
            `<p>Avec ${lait} L de lait, on obtient 1 kg de beurre.</p>`,
            '<p><b>Pour obtenir 100 g de beurre, il faut … de lait.</b></p>',
        ),
        L11,
    );
};

/**
 * Q12 : eau économisée, douche ou bain (une étape, proportionnalité).
 * Item : « Quand Romain prend sa douche, il ouvre le robinet au maximum pendant 8 minutes et il utilise
 *   80 litres d'eau. Quand il prend un bain, il ouvre le même robinet au maximum pendant 12 minutes.
 *   Quel volume d'eau économise-t-il en choisissant de prendre une douche à la place d'un bain ? » ;
 *   40 L (juste), 160 L, 10 L, 120 L. Réécrit sans « il » : « Pour sa douche, Romain ouvre… »,
 *   « Quel volume d'eau est économisé si Romain prend une douche à la place d'un bain ? ».
 * Domaine : l'item, une douche de 8 minutes et 80 L (10 L par minute), un bain de 12 minutes.
 *   Variantes : une douche de 6 à 12 minutes (nombre pair), un bain d'une fois et demie la douche,
 *   comme 8 et 12 ; un débit de 8 à 12 L par minute. 20 variantes.
 * Erreurs : 10 L : le débit, la première étape seulement ; 120 L : le volume du bain, la soustraction
 *   oubliée ; 160 L : erreur non identifiée, TRANCHÉ LE 10/10 (on garde) : 80 × 2 (le double de la douche) ou 80 × 8 ÷ 4
 *   (le rapport des 4 minutes d'écart pris à l'envers) ; les deux lectures restent égales dans les
 *   variantes, où le bain dure toujours une fois et demie la douche.
 */
const L12 = 'Eau économisée, douche ou bain';
const DOMAINE_Q12 = [];
[6, 8, 10, 12].forEach((douche) => {
    for (let debit = 8; debit <= 12; debit += 1) DOMAINE_Q12.push({ douche, debit });
});
const genQ12 = () => {
    const { douche, debit } = randomChoice(DOMAINE_Q12);
    const bain = (douche * 3) / 2;
    const vDouche = debit * douche;
    const vBain = debit * bain;
    const juste = vBain - vDouche;
    verifier(juste === debit * (bain - douche), 'Q12, l\'eau économisée');
    verifier((vDouche * douche) / (bain - douche) === 2 * vDouche, 'Q12, les deux lectures du piège');
    return qcm(
        [`${juste} L`, `${2 * vDouche} L`, `${debit} L`, `${vBain} L`],
        html(
            `<p>Pour sa douche, Romain ouvre le robinet au maximum pendant ${douche} minutes et utilise ${vDouche} litres d'eau.</p>`,
            `<p>Pour un bain, Romain ouvre le même robinet au maximum pendant ${bain} minutes.</p>`,
            '<p><b>Quel volume d\'eau est économisé si Romain prend une douche à la place d\'un bain ?</b></p>',
        ),
        L12,
    );
};

/**
 * Q13 : aire hachurée en fonction de a et b (deux étapes, mixte additif et multiplicatif).
 * Item : « On considère la figure suivante. [un rectangle de côtés a (hauteur) et b (largeur) ; dans
 *   le coin en bas à droite, un rectangle hachuré de hauteur 3 ; le reste du côté du bas mesure a ;
 *   angles droits marqués] On souhaite exprimer l'aire de la surface hachurée en fonction de a et b. » ;
 *   3(b – a) (juste), 3(b + a), 3a, 3b.
 * Domaine : l'item, une hauteur hachurée de 3, en bas à droite. Variantes : la hauteur hachurée de 2
 *   à 9 ; le rectangle hachuré dans l'un des quatre coins (figure retournée, mêmes cotes) ; les
 *   lettres a et b de l'item. 32 variantes. Choix en KaTeX.
 * Erreurs : 3(b + a) : la longueur b + a au lieu de b − a ; 3a : le segment de longueur a pris pour
 *   la longueur hachurée ; 3b : toute la longueur b prise.
 */
const L13 = 'Aire hachurée en fonction de a et b';
const DOMAINE_Q13 = [];
for (let k = 2; k <= 9; k += 1) {
    [false, true].forEach((inverseX) => [false, true].forEach((inverseY) => DOMAINE_Q13.push({ k, inverseX, inverseY })));
}
const figureHachuree = ({ k, inverseX, inverseY }, alt) => {
    const X0 = 56;
    const Y0 = 46;
    const LB = 280; // le côté b
    const HA = 126; // le côté a, et le segment a du bas
    const HK = 42; // la hauteur hachurée
    const fx = (x) => (inverseX ? 2 * X0 + LB - x : x);
    const fy = (y) => (inverseY ? 2 * Y0 + HA - y : y);
    const pt = (x, y) => `${fx(x)} ${fy(y)}`;
    const bx0 = X0 + HA;
    const bx1 = X0 + LB;
    const by0 = Y0 + HA - HK;
    const by1 = Y0 + HA;
    let hachures = '';
    for (let s = bx0 + by0 + 8; s < bx1 + by1; s += 8) {
        const ya = Math.max(by0, s - bx1);
        const yb = Math.min(by1, s - bx0);
        if (yb > ya) hachures += `M${pt(s - ya, ya)}L${pt(s - yb, yb)}`;
    }
    const angle = (x, y, sx, sy) => `M${pt(x, y)}L${pt(x + sx * 10, y)}L${pt(x + sx * 10, y + sy * 10)}L${pt(x, y + sy * 10)}Z`;
    const angles = [
        angle(X0, Y0, 1, 1), angle(X0 + LB, Y0, -1, 1), angle(X0, Y0 + HA, 1, -1),
        angle(bx0, by0, 1, 1), angle(bx1, by0, -1, 1), angle(bx0, by1, 1, -1), angle(bx1, by1, -1, -1),
    ].join('');
    const fleche = (x1, y1, x2, y2) => {
        const lg = Math.hypot(x2 - x1, y2 - y1);
        const ux = (x2 - x1) / lg;
        const uy = (y2 - y1) / lg;
        const pointe = (x, y, sens) => `M${pt(x, y)}L${pt(x - sens * ux * 8 - uy * 4, y - sens * uy * 8 + ux * 4)}L${pt(x - sens * ux * 8 + uy * 4, y - sens * uy * 8 - ux * 4)}Z`;
        return `<path d="M${pt(x1, y1)}L${pt(x2, y2)}" stroke="#374151" stroke-width="1.4"/><path d="${pointe(x2, y2, 1)}${pointe(x1, y1, -1)}" fill="#374151"/>`;
    };
    const etiquette = (x, y, dx, dy, texte, lettre) => html(
        `<text x="${fx(x) + (inverseX ? -dx : dx)}" y="${fy(y) + (inverseY ? -dy : dy)}" text-anchor="middle" dominant-baseline="central"`,
        lettre ? ' font-family="KaTeX_Math, \'Times New Roman\', serif" font-style="italic" font-size="20"' : ' font-size="17"',
        `>${texte}</text>`,
    );
    const contenu = html(
        `<path d="M${pt(bx0, by0)}L${pt(bx1, by0)}L${pt(bx1, by1)}L${pt(bx0, by1)}Z" fill="#f8e6df"/>`,
        `<path d="${hachures}" stroke="#b5412b" stroke-width="1.3"/>`,
        `<path d="${angles}" fill="#e3f1e7" stroke="#2e8b57" stroke-width="1.2"/>`,
        `<path d="M${pt(bx0, by0)}L${pt(bx1, by0)}L${pt(bx1, by1)}L${pt(bx0, by1)}Z" fill="none" stroke="#b5412b" stroke-width="2"/>`,
        `<path d="M${pt(X0, Y0)}L${pt(X0 + LB, Y0)}L${pt(X0 + LB, Y0 + HA)}L${pt(X0, Y0 + HA)}Z" fill="none" stroke="#1f2937" stroke-width="2"/>`,
        fleche(X0, Y0 - 20, X0 + LB, Y0 - 20),
        etiquette(X0 + LB / 2, Y0 - 20, 0, -13, 'b', true),
        fleche(X0 - 20, Y0, X0 - 20, Y0 + HA),
        etiquette(X0 - 20, Y0 + HA / 2, -13, 0, 'a', true),
        fleche(X0, Y0 + HA + 20, X0 + HA, Y0 + HA + 20),
        etiquette(X0 + HA / 2, Y0 + HA + 20, 0, 14, 'a', true),
        fleche(X0 + LB + 18, by0, X0 + LB + 18, by1),
        etiquette(X0 + LB + 18, (by0 + by1) / 2, 14, 0, String(k), false),
    );
    return svgRacine(2 * X0 + LB, 2 * Y0 + HA, alt, contenu);
};
const genQ13 = () => {
    const tirage = randomChoice(DOMAINE_Q13);
    const { k } = tirage;
    // second calcul : grand rectangle moins la bande du haut et le rectangle à côté de la hachure
    const a = k + 4;
    const b = a + 6;
    verifier(a * b - (a - k) * b - k * a === k * (b - a), 'Q13, l\'aire hachurée');
    const alt = `Rectangle de côtés a et b. Dans un coin, un rectangle hachuré de hauteur ${k} s'appuie sur le côté de longueur b ; le reste de ce côté mesure a.`;
    return qcm(
        [tex(`${k}(b - a)`), tex(`${k}(b + a)`), tex(`${k}a`), tex(`${k}b`)],
        html(
            '<p>On considère la figure suivante.</p>{{element:0}}',
            `<p><b>On souhaite exprimer l'aire de la surface hachurée en fonction de ${tex('a')} et ${tex('b')}.</b></p>`,
        ),
        L13,
        [image(figureHachuree(tirage, alt), alt, 'medium')],
    );
};

/**
 * Q14 : largeur d'un rectangle, connaissant son aire (deux étapes, mixte).
 * Item : « On veut carreler un salon de 56 m². Ce salon a la forme d'un rectangle de longueur 8 m.
 *   Quelle est la largeur du salon ? » ; 7 m (juste), 64 m, 14 m, 48 m.
 * Domaine : l'item, 56 m², une longueur de 8 m, une largeur de 7 m (tables). Variantes : une longueur
 *   de 8 à 12 m, une largeur entière plus petite, d'au moins 0,4 fois la longueur, une aire d'au plus
 *   96 m², multiple de 4 (le piège « aire ÷ 4 » reste entier) ; jamais une largeur de 4 m (l'aire ÷ 4
 *   serait la longueur). 11 variantes.
 * Erreurs : 64 m : 56 + 8 ; 48 m : 56 − 8 ; 14 m : erreur non identifiée, TRANCHÉ LE 10/10 (on garde) : deux lectures
 *   donnent 14 sur l'item, 56 ÷ 4 (l'aire partagée entre les quatre côtés) et 2 × 7 (le double de la
 *   largeur) ; retenue : 56 ÷ 4, une opération sur les données comme les deux autres pièges.
 */
const L14 = 'Largeur d\'un rectangle, connaissant son aire';
const DOMAINE_Q14 = [];
for (let longueur = 8; longueur <= 12; longueur += 1) {
    for (let largeur = 3; largeur < longueur; largeur += 1) {
        const aire = longueur * largeur;
        if (aire <= 96 && aire % 4 === 0 && largeur !== 4 && largeur >= 0.4 * longueur) DOMAINE_Q14.push({ longueur, largeur, aire });
    }
}
const genQ14 = () => {
    const { longueur, largeur, aire } = randomChoice(DOMAINE_Q14);
    verifier(aire % longueur === 0 && aire / longueur === largeur, 'Q14, la largeur');
    return qcm(
        [`${largeur} m`, `${aire + longueur} m`, `${aire / 4} m`, `${aire - longueur} m`],
        html(
            `<p>On veut carreler un salon de ${aire} m². Ce salon a la forme d'un rectangle de longueur ${longueur} m.</p>`,
            '<p><b>Quelle est la largeur du salon ?</b></p>',
        ),
        L14,
    );
};

/**
 * Q15 : durée de fabrication et temps disponible (une étape, multiplicatif, durées).
 * Item : « Un ébéniste fabrique des figurines en bois. La réalisation complète d'une figurine nécessite
 *   17 minutes. On lui demande de réaliser 11 figurines en trois heures. Est-ce possible s'il
 *   travaille sans interruption ? » ; NON. Il lui manquera sept minutes. (juste) ; OUI. Il pourra
 *   fabriquer douze figurines. ; NON. Il pourra fabriquer seulement neuf figurines. ; OUI. Il lui
 *   faudra moins de deux heures. (« il » est l'ébéniste, qui n'est pas nommé.)
 * Domaine : l'item, 17 minutes par figurine, 11 figurines, trois heures (187 minutes, 7 de trop).
 *   Variantes : deux, trois ou quatre heures ; une figurine de 11 à 19 minutes, jamais un multiple de 5 ;
 *   un nombre de figurines dont la durée dépasse le temps donné de 2 à 19 minutes. Les deux arrondis
 *   au multiple de 5 tombent juste dans le temps donné ; l'arrondi par défaut promet au moins la
 *   commande (OUI), l'arrondi par excès moins (NON). 28 variantes.
 * Erreurs : douze : 180 ÷ 15, la durée arrondie à 15 minutes ; neuf : 180 ÷ 20, arrondie à
 *   20 minutes ; moins de deux heures : 11 × 17 = 187 minutes lu 1 h 87 (1,87 h), d'où « moins de
 *   deux heures » (en quatre heures : 250 minutes lu 2 h 50, « moins de trois heures »).
 */
const L15 = 'Durée de fabrication et temps disponible';
const DOMAINE_Q15 = [];
[2, 3, 4].forEach((heures) => {
    [11, 12, 13, 14, 16, 17, 18, 19].forEach((duree) => {
        const bas = 5 * Math.floor(duree / 5);
        const parDefaut = (60 * heures) / bas;
        const parExces = (60 * heures) / (bas + 5);
        for (let n = 5; n <= 30; n += 1) {
            const manque = n * duree - 60 * heures;
            const lu = Math.floor((n * duree) / 100) + 1;
            if (manque >= 2 && manque <= 19 && Number.isInteger(parDefaut) && Number.isInteger(parExces)
                && parDefaut >= n && parExces < n && lu <= heures) {
                DOMAINE_Q15.push({ heures, duree, n, manque, parDefaut, parExces, lu });
            }
        }
    });
});
const genQ15 = () => {
    const { heures, duree, n, manque, parDefaut, parExces, lu } = randomChoice(DOMAINE_Q15);
    // second calcul : la durée totale en heures et minutes, et le nombre de figurines faites à temps
    const total = n * duree;
    verifier(Math.floor(total / 60) === heures && total % 60 === manque, 'Q15, ce qui manque');
    verifier(Math.floor((60 * heures) / duree) < n, 'Q15, la commande impossible');
    return qcm(
        [
            `NON. Il lui manquera ${enLettres(manque)} minutes.`,
            `OUI. Il pourra fabriquer ${enLettres(parDefaut)} figurines.`,
            `NON. Il pourra fabriquer seulement ${enLettres(parExces)} figurines.`,
            `OUI. Il lui faudra moins de ${enLettres(lu)} heures.`,
        ],
        html(
            `<p>Un ébéniste fabrique des figurines en bois. La réalisation complète d'une figurine nécessite ${duree} minutes. `,
            `On lui demande de réaliser ${n} figurines en ${enLettres(heures)} heures.</p>`,
            '<p><b>Est-ce possible s\'il travaille sans interruption ?</b></p>',
        ),
        L15,
    );
};

/**
 * Q16 : pages lues, 10 % par jour (deux étapes, pourcentage, proportionnalité).
 * Item : « Anna lit un livre de 110 pages. Chaque jour elle en lit 10 %. Combien lit-elle de pages en
 *   7 jours ? » ; 77 pages (juste), 70 pages, 10 pages, 11 pages. Réécrit sans « elle » : « Chaque
 *   jour, Anna en lit 10 %. », « Quel est le nombre de pages lues par Anna en 7 jours ? ».
 * Domaine : l'item, 110 pages, 10 % par jour, 7 jours. Variantes : toujours 10 % par jour ; un livre
 *   de 80 à 250 pages (dizaines), jamais 100 (10 % ferait 10 pages, la valeur d'un piège) ; de 3 à
 *   9 jours.
 * Erreurs : 70 pages : 10 % lu comme 10 pages, fois 7 ; 10 pages : 10 % lu comme 10 pages, un seul
 *   jour ; 11 pages : 10 % de 110 = 11, les 7 jours oubliés.
 */
const L16 = 'Pages lues, 10 % par jour';
const DOMAINE_Q16 = [];
for (let pages = 80; pages <= 250; pages += 10) {
    for (let jours = 3; jours <= 9; jours += 1) {
        if (pages !== 100) DOMAINE_Q16.push({ pages, jours });
    }
}
const genQ16 = () => {
    const { pages, jours } = randomChoice(DOMAINE_Q16);
    const parJour = pages / 10;
    verifier((pages * 10) % 100 === 0 && (pages * 10) / 100 === parJour && jours * parJour <= pages, 'Q16, les pages par jour');
    return qcm(
        [`${jours * parJour} pages`, `${10 * jours} pages`, '10 pages', `${parJour} pages`],
        html(
            `<p>Anna lit un livre de ${pages} pages. Chaque jour, Anna en lit 10 %.</p>`,
            `<p><b>Quel est le nombre de pages lues par Anna en ${jours} jours ?</b></p>`,
        ),
        L16,
    );
};

/**
 * Q17 : masse de safran produite (une étape, proportionnalité, conversion).
 * Item : « Il faut 80 g de fleurs de crocus pour obtenir 1 g de safran. Quelle masse de safran un
 *   producteur pourra-t-il produire avec 1kg de fleurs de crocus ? » ; 12,5 g (juste), 80 g,
 *   12,5 kg, 80 kg.
 * Domaine : l'item, 80 g de fleurs pour 1 g de safran, 1 kg de fleurs, une réponse décimale, 12,5 g.
 *   Variantes : de 40 à 400 g de fleurs pour 1 g de safran, de 1 à 5 kg de fleurs ; la réponse est un
 *   décimal à un chiffre après la virgule, 5, comme 12,5 (avec 1 kg seulement, 80 et 400 g sont les
 *   seuls à le donner). 11 variantes.
 * Erreurs : 80 g : la donnée recopiée ; 80 kg : 1 kg multiplié par 80 au lieu de 1000 g divisés par
 *   80 ; 12,5 kg : le bon calcul, en grammes, écrit en kilogrammes.
 */
const L17 = 'Masse de safran produite';
const DOMAINE_Q17 = [];
for (let kg = 1; kg <= 5; kg += 1) {
    for (let fleurs = 40; fleurs <= 400; fleurs += 1) {
        const doubles = (2000 * kg) / fleurs;
        if (Number.isInteger(doubles) && doubles % 2 === 1) DOMAINE_Q17.push({ kg, fleurs, dixiemes: doubles * 5 });
    }
}
const genQ17 = () => {
    const { kg, fleurs, dixiemes: d } = randomChoice(DOMAINE_Q17);
    verifier(d * fleurs === 10000 * kg && d % 10 === 5, 'Q17, la masse de safran');
    return qcm(
        [`${dixiemes(d)} g`, `${fleurs} g`, `${kg * fleurs} kg`, `${dixiemes(d)} kg`],
        html(
            `<p>Il faut ${fleurs} g de fleurs de crocus pour obtenir 1 g de safran.</p>`,
            `<p><b>Quelle masse de safran un producteur pourra-t-il produire avec ${kg} kg de fleurs de crocus ?</b></p>`,
        ),
        L17,
    );
};

/**
 * Q18 : pourcentage de droitiers (plusieurs étapes, proportionnalité).
 * Item : « Dans une fratrie de 5 enfants, un seul est gaucher et les autres sont droitiers. Quel est
 *   le pourcentage d'enfants droitiers dans cette fratrie ? » ; 80 % (juste), 75 %, 4 %, 20 %.
 * Domaine : l'item, une fratrie de 5 enfants, un gaucher, quatre pourcentages entiers. Décidé par le prof
 *   le 10/10 : des pièges exacts, jamais d'arrondi. Avec moins de gauchers que de droitiers, seul le
 *   rapport 1 gaucher pour 4 droitiers rend entiers à la fois la bonne réponse et le piège partie sur
 *   partie : la réponse reste donc 80 %, comme les pièges 75 % et 20 % ; seul le nombre de droitiers
 *   écrit en pourcentage change. Les variantes changent la taille et le cadre du groupe, un gaucher sur
 *   cinq personnes : fratrie de 5, groupe de 10, 15 ou 20 enfants, équipe de 15 joueurs, classe de 20 ou
 *   30 élèves, club de 30, 40, 45 ou 50 adhérents (jamais 25 : 20 droitiers, soit « 20 % », doublerait
 *   le pourcentage de gauchers). 11 variantes.
 * Erreurs : 75 % : 1 gaucher pour 4 droitiers lu « 1 sur 4 », 25 % de gauchers, donc 75 % de
 *   droitiers ; 4 % : le nombre de droitiers écrit en pourcentage ; 20 % : le pourcentage de
 *   gauchers, l'autre groupe.
 */
const L18 = 'Pourcentage de droitiers';
// Un gaucher sur cinq personnes : la seule proportion où les quatre choix tombent juste (fiche ci-dessus).
const CADRES_Q18 = [
    { tailles: [5], debut: 'une fratrie de', membres: 'enfants', deMembres: "d'enfants", fin: 'cette fratrie' },
    { tailles: [10, 15, 20], debut: 'un groupe de', membres: 'enfants', deMembres: "d'enfants", fin: 'ce groupe' },
    { tailles: [15], debut: 'une équipe de', membres: 'joueurs', deMembres: 'de joueurs', fin: 'cette équipe' },
    { tailles: [20, 30], debut: 'une classe de', membres: 'élèves', deMembres: "d'élèves", fin: 'cette classe' },
    { tailles: [30, 40, 45, 50], debut: 'un club de', membres: 'adhérents', deMembres: "d'adhérents", fin: 'ce club' },
];
const choixQ18 = (n, g) => {
    const droitiers = n - g;
    return [
        `${(100 * droitiers) / n} %`,
        `${100 - (100 * g) / droitiers} %`,
        `${droitiers} %`,
        `${(100 * g) / n} %`,
    ];
};
const DOMAINE_Q18 = [];
CADRES_Q18.forEach((cadre) => cadre.tailles.forEach((n) => {
    const g = n / 5;
    if (Number.isInteger(g) && new Set(choixQ18(n, g)).size === 4) DOMAINE_Q18.push({ cadre, n, g });
}));
const genQ18 = () => {
    const { cadre, n, g } = randomChoice(DOMAINE_Q18);
    const choix = choixQ18(n, g);
    const droitiers = n - g;
    verifier(
        ((100 * droitiers) / n) + ((100 * g) / n) === 100
            && Number.isInteger((100 * droitiers) / n)
            && Number.isInteger((100 * g) / droitiers),
        'Q18, les droitiers',
    );
    const gauchers = g === 1 ? 'un seul est gaucher' : `${g} sont gauchers`;
    return qcm(
        choix,
        html(
            `<p>Dans ${cadre.debut} ${n} ${cadre.membres}, ${gauchers} et les autres sont droitiers.</p>`,
            `<p><b>Quel est le pourcentage ${cadre.deMembres} droitiers dans ${cadre.fin} ?</b></p>`,
        ),
        L18,
    );
};

/**
 * Q19 : composition de l'œuf, le tableau correct (une étape, pourcentages, proportionnalité).
 * Item : « Voici la composition de l'œuf. [diagramme circulaire : coquille 10 %, blanc 60 %, jaune
 *   sans pourcentage] En moyenne, un œuf pèse 60 g. Lequel des tableaux ci-dessous est correct ? » ;
 *   quatre tableaux en image, colonnes Œuf, Coquille, Blanc, Jaune, lignes Masse (g) et % :
 *   Image B, masses 60, 6, 36, 18 et pourcentages 100, 10, 60, 30 (juste) ; Image H, 60, 10, 60, 30
 *   et 60, 10, 60, 30 ; Image HH, 60, 6, 60, 30 et 100, 10, 60, 30 ; Image BB, 60, 10, 36, 18 et
 *   100, 10, 60, 30.
 * Mode auto, TRANCHÉ LE 10/10 (on garde) : les choix officiels sont des tableaux en image ; les quatre tableaux vont
 *   dans une image SVG de l'énoncé, étiquetés A, B, C, D, et les choix sont « A », « B », « C »,
 *   « D », dans cet ordre (la place du tableau juste est tirée au sort).
 * Domaine : l'item, un œuf de 60 g, coquille 10 %, blanc 60 %, jaune 30 %, masses entières.
 *   Variantes : un œuf de 50, 60, 70 ou 80 g ; une coquille de 10 ou 12 %, un blanc de 55 à 65 %, le
 *   jaune le reste (25 à 35 %), le diagramme sans le pourcentage du jaune, comme l'item ; toutes les
 *   masses entières. 15 compositions, quatre places pour le tableau juste.
 * Erreurs : Image H : chaque pourcentage pris pour une masse en grammes, et l'œuf noté 60 %, comme sa
 *   masse : pourcentage et masse confondus ; Image HH : seule la coquille calculée (10 % de 60 g, le
 *   calcul le plus simple), les pourcentages du blanc et du jaune recopiés comme masses ; Image BB :
 *   le blanc et le jaune calculés, le pourcentage de la coquille recopié comme masse.
 */
const L19 = 'Composition de l\'œuf, le tableau correct';
const LETTRES_Q19 = ['A', 'B', 'C', 'D'];
const DOMAINE_Q19 = [];
[[50, [10, 12], [56, 58, 60, 62]], [60, [10], [55, 60, 65]], [70, [10], [60]], [80, [10], [55, 60, 65]]]
    .forEach(([masse, coquilles, blancs]) => coquilles.forEach((c) => blancs.forEach((b) => {
        DOMAINE_Q19.push({ masse, c, b, j: 100 - c - b });
    })));
const camembert = (c, b, alt) => {
    const cx = 150;
    const cy = 138;
    const r = 116;
    const point = (deg, rayon = r) => {
        const t = (deg * Math.PI) / 180;
        return [cx + rayon * Math.sin(t), cy - rayon * Math.cos(t)];
    };
    const secteur = (d1, d2, couleur) => {
        const [x1, y1] = point(d1);
        const [x2, y2] = point(d2);
        return `<path d="M${cx} ${cy}L${x1.toFixed(1)} ${y1.toFixed(1)}A${r} ${r} 0 ${d2 - d1 > 180 ? 1 : 0} 1 ${x2.toFixed(1)} ${y2.toFixed(1)}Z" fill="${couleur}" stroke="#4b5563" stroke-width="1.5"/>`;
    };
    const texte = (deg, rayon, taille, lignes) => {
        const [x, y] = point(deg, rayon);
        return lignes.map(([mot, gras], i) => `<text x="${x.toFixed(1)}" y="${(y + (i - (lignes.length - 1) / 2) * 15 + 4).toFixed(1)}" text-anchor="middle" font-size="${taille}"${gras ? ' font-weight="700"' : ''}>${mot}</text>`).join('');
    };
    const d0 = 12;
    const d1 = d0 + 3.6 * c;
    const d2 = d1 + 3.6 * b;
    const contenu = html(
        secteur(d0, d1, '#d9d9d9'),
        secteur(d1, d2, '#ffffff'),
        secteur(d2, d0 + 360, '#f3ef86'),
        texte((d0 + d1) / 2, 0.79 * r, 11.5, [['Coquille', false], [`${c} %`, true]]),
        texte((d1 + d2) / 2, 0.5 * r, 13, [['Blanc', false], [`${b} %`, true]]),
        texte((d2 + d0 + 360) / 2, 0.56 * r, 13, [['Jaune', false]]),
    );
    return svgRacine(300, 276, alt, contenu);
};
const tableauxOeuf = (tableaux, alt) => {
    const C0 = 78;
    const C = 60;
    const LG = 26;
    const LT = C0 + 4 * C;
    const HT = 3 * LG;
    const EX = 34;
    const EY = 44;
    const MX = 12;
    const MY = 30;
    const entetes = ['', 'Œuf', 'Coquille', 'Blanc', 'Jaune'];
    const contenu = tableaux.map((t, i) => {
        const x = MX + (i % 2) * (LT + EX);
        const y = MY + Math.floor(i / 2) * (HT + EY);
        const lignes = [entetes, ['Masse (g)', ...t.masses], ['%', ...t.pourcents]];
        let traits = '';
        for (let r = 1; r < 3; r += 1) traits += `M${x} ${y + r * LG}H${x + LT}`;
        for (let col = 0; col < 4; col += 1) traits += `M${x + C0 + col * C} ${y}V${y + HT}`;
        const cellules = lignes.map((cases, r) => cases.map((v, col) => {
            const xc = col === 0 ? x + C0 / 2 : x + C0 + (col - 1) * C + C / 2;
            return `<text x="${xc}" y="${y + r * LG + 18}" text-anchor="middle" font-size="13">${v}</text>`;
        }).join('')).join('');
        return html(
            `<text x="${x}" y="${y - 10}" font-size="17" font-weight="700">${LETTRES_Q19[i]}</text>`,
            `<rect x="${x}" y="${y}" width="${LT}" height="${HT}" fill="#ffffff" stroke="#1e3a8a" stroke-width="1.6"/>`,
            `<path d="${traits}" stroke="#1e3a8a" stroke-width="1.1" fill="none"/>`,
            cellules,
        );
    }).join('');
    return svgRacine(2 * MX + 2 * LT + EX, MY + 2 * HT + EY + 12, alt, contenu);
};
const genQ19 = () => {
    const { masse, c, b, j } = randomChoice(DOMAINE_Q19);
    const [mc, mb, mj] = [c, b, j].map((p) => (masse * p) / 100);
    const pourcents = [100, c, b, j];
    const tableaux = shuffle([
        { masses: [masse, mc, mb, mj], pourcents, juste: true },
        { masses: [masse, c, b, j], pourcents: [masse, c, b, j] },
        { masses: [masse, mc, b, j], pourcents },
        { masses: [masse, c, mb, mj], pourcents },
    ]);
    // second calcul : un tableau est correct si ses pourcentages sont ceux de l'œuf et chaque masse
    // vaut son pourcentage de la masse de l'œuf ; un seul l'est
    const corrects = tableaux.map((t) => t.pourcents.every((p, i) => p === pourcents[i])
        && t.masses.every((m, i) => Number.isInteger(m) && m * 100 === masse * pourcents[i]));
    verifier(corrects.filter(Boolean).length === 1 && tableaux[corrects.indexOf(true)].juste, 'Q19, un seul tableau correct');
    const lettre = LETTRES_Q19[corrects.indexOf(true)];
    const altCamembert = `Diagramme circulaire de la composition de l'œuf : coquille ${c} %, blanc ${b} %, jaune sans pourcentage indiqué.`;
    const altTableaux = 'Quatre tableaux, A, B, C et D, donnant la masse en grammes et le pourcentage de l\'œuf, de la coquille, du blanc et du jaune.';
    const payload = qcm(
        [lettre, ...LETTRES_Q19.filter((l) => l !== lettre)],
        html(
            '<p>Voici la composition de l\'œuf.</p>{{element:0}}',
            `<p>En moyenne, un œuf pèse ${masse} g.</p>`,
            '<p><b>Lequel des tableaux ci-dessous est correct ?</b></p>{{element:1}}',
        ),
        L19,
        [image(camembert(c, b, altCamembert), altCamembert, 'small'), image(tableauxOeuf(tableaux, altTableaux), altTableaux, 'large')],
    );
    // les choix restent dans l'ordre A, B, C, D, celui des étiquettes du dessin
    payload.contenu = { ...payload.contenu, choix: [...LETTRES_Q19], bonne_reponse: lettre };
    return payload;
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_qcm_unique: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
    q11_qcm_unique: genQ11,
    q12_qcm_unique: genQ12,
    q13_qcm_unique: genQ13,
    q14_qcm_unique: genQ14,
    q15_qcm_unique: genQ15,
    q16_qcm_unique: genQ16,
    q17_qcm_unique: genQ17,
    q18_qcm_unique: genQ18,
    q19_qcm_unique: genQ19,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: '10 €' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: '40 m' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: '4 min' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: '7,20 €' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: '33 €' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: '20 km' },
    { questionNum: 'Q07', formatKey: 'q07_qcm_unique', libelle: L07, bonneReponseOfficielle: '9 km²' },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: 'à la pomme.' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: '20 m' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: '3/4' },
    { questionNum: 'Q11', formatKey: 'q11_qcm_unique', libelle: L11, bonneReponseOfficielle: '2 L' },
    { questionNum: 'Q12', formatKey: 'q12_qcm_unique', libelle: L12, bonneReponseOfficielle: '40 L' },
    { questionNum: 'Q13', formatKey: 'q13_qcm_unique', libelle: L13, bonneReponseOfficielle: '3(b – a)' },
    { questionNum: 'Q14', formatKey: 'q14_qcm_unique', libelle: L14, bonneReponseOfficielle: '7 m' },
    { questionNum: 'Q15', formatKey: 'q15_qcm_unique', libelle: L15, bonneReponseOfficielle: 'NON. Il lui manquera sept minutes.' },
    { questionNum: 'Q16', formatKey: 'q16_qcm_unique', libelle: L16, bonneReponseOfficielle: '77 pages' },
    { questionNum: 'Q17', formatKey: 'q17_qcm_unique', libelle: L17, bonneReponseOfficielle: '12,5 g' },
    { questionNum: 'Q18', formatKey: 'q18_qcm_unique', libelle: L18, bonneReponseOfficielle: '80 %' },
    { questionNum: 'Q19', formatKey: 'q19_qcm_unique', libelle: L19, bonneReponseOfficielle: 'Image B' },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
